import get from 'lodash/get';
import { getIsRecentSignUp } from '../../../actions/AccountActions/AccountActions';
import { localStorageService } from '../localStorageService';
import { Permission } from '../../../types';

interface CustomWindow extends Window {
  dataLayer: any[];
  gtag: (...args: any[]) => void;
  lintrk: (...args: any[]) => void;
  fbq: (type: string, action?: string, options?: any) => void;
}

declare var window: CustomWindow;

export interface EventOptions {
  event_category?: string;
  event_label?: string;
  value?: number;
  // google ads conversion events (BRAINTECH-2816)
  send_to?: string;
  currency?: string;
  // GA4 recommended event "view_search_results"
  search_term?: string;
  // gtag transport method; 'beacon' uses navigator.sendBeacon so the event
  // is not dropped when the click triggers a same-tab page unload (BRAINTECH-5235)
  transport_type?: string;
}

interface AnalyticsService {
  trackPage(): void;
  trackCustomEvent(event: string, options?: EventOptions): void;
  trackPredefinedEvent(event: string, data?: any): void;
  trackCustomEventOnce: (event: string, options?: EventOptions) => void;
  setUserInfo(userId: string, permissions: Permission[]): void;
  setUserLevel(level: string | null | undefined): void;
  clearUserId(): void;
  getFromCache: (path: string) => any;
  setCache: (path: string, value: any) => void;
  resetCache: () => void;
}

//todo: change cache to Map
let _cache: Record<string, any> = {};

export function getUserType(permissions: Permission[] = []) {
  const matchingPermissions: Permission[] = ['RESEARCHER', 'WORLDQUANT', 'CONSULTANT'];
  const permissionSet = new Set(permissions);
  for (const userType of matchingPermissions) {
    if (permissionSet.has(userType)) {
      return userType;
    }
  }
  return 'GENERAL';
}

const analytics: AnalyticsService = {
  getFromCache: (path: string) => get(_cache, path),
  setCache: (path: string, value: any) => (_cache[path] = value),
  resetCache: () => (_cache = {}),

  trackPage(): void {
    // Google Tag Manager automatically watches url change events
    // so this is not required: (window as any).gtag('event', 'page_view')
  },

  setUserInfo(userId: string, permissions) {
    if (!('gtag' in window)) return;
    const userType = getUserType(permissions);
    window.gtag('set', { user_id: userId, user_type: userType });
    window.gtag('set', 'user_properties', { user_type: userType });
  },

  setUserLevel(level) {
    if (!('gtag' in window)) return;
    if (!level) return;
    if (level.toUpperCase() === 'UNASSIGNED') return;
    window.gtag('set', 'user_properties', { user_level: level });
  },

  clearUserId() {
    if (!('gtag' in window)) return;
    window.gtag('set', { user_id: null });
    window.gtag('set', 'user_properties', { user_level: null });
  },

  trackCustomEvent(event: string, options?: EventOptions): void {
    // Google Tag Manager
    if ('gtag' in window) {
      window.gtag('event', event, options);
    }

    // Facebook Pixel
    if ('fbq' in window) {
      window.fbq('trackCustom', event, options);
    }

    // LinkedIn Pixel
    // LinkedIn Pixel may not support sending custom events at this time
    // Please use trackPredefinedEvent instead if you want to send events to LinkedIn Pixel
  },

  trackCustomEventOnce(event: string, options?: EventOptions): void {
    const key = JSON.stringify([event, Object.values(options || {}).sort()]);
    const cached = this.getFromCache(key);
    if (!cached) {
      this.trackCustomEvent(event, options);
      this.setCache(key, true);
    }
  },

  trackPredefinedEvent(event: string, data: any = {}): void {
    // Google Tag Manager
    // https://developers.google.com/analytics/devguides/collection/gtagjs/events
    const GOOGLE_TAG_MANAGER_EVENTS: { [key: string]: [string] | [string, EventOptions] } = {
      SignUp: ['sign_up'],
      SignIn: ['login'],
      EmailVerified: ['email_verify', { event_category: 'engagement' }],
      ProfileMenu: ['open_profile_menu'],
      PlayVideo: ['Play', { event_category: 'video', event_label: data.name }],
      WatchVideo: [`Watched ${data.percent}%`, { event_category: 'video', event_label: data.name }],
    };

    const googleTagManagerEvent = GOOGLE_TAG_MANAGER_EVENTS[event];
    if (googleTagManagerEvent && 'gtag' in window) {
      window.gtag('event', ...googleTagManagerEvent);
    }

    // Facebook
    if ('fbq' in window) {
      window.fbq('track', event);
    }

    // LinkedIn
    const LINKEDIN_EVENTS: { [key: string]: string } = {
      SignUp: '535827',
      EmailVerified: '535835',
    };

    const linkedinEvent = LINKEDIN_EVENTS[event];
    if (linkedinEvent && 'lintrk' in window) {
      window.lintrk('track', { conversion_id: linkedinEvent });
    }
  },
};

let isExperimentAssigned = false;
export type ABTestGroups = typeof AB_TEST_GROUPS[keyof typeof AB_TEST_GROUPS];

export const AB_TEST_GROUPS = {
  oldForm: 'old',
  newForm: 'new',
} as const;

export const GOOGLE_AB_EXPERIMENT_ID = 'sign_up_wizard';
const AB_DISTRIBUTION = 0.5;

export function assignSignupExperimentVariant() {
  try {
    if (isExperimentAssigned) {
      return;
    }
    let group = getABTestGroup();
    if (!group) {
      group = Math.random() < AB_DISTRIBUTION ? AB_TEST_GROUPS.oldForm : AB_TEST_GROUPS.newForm;
      setABTestGroup(group);
    }

    let experimentId = GOOGLE_AB_EXPERIMENT_ID;
    if (!getIsRecentSignUp() && 'gtag' in window) {
      window.gtag('event', 'experiment_impression', {
        experiment_id: experimentId,
        variant_id: `${experimentId}.${group}`,
      });

      //this event is used to create a new audience for each experiment group:
      analytics.trackCustomEvent('experiment_group', { event_label: `${experimentId}.${group}` });
    }
    isExperimentAssigned = true;
  } catch (e) {
    console.log(e);
  }
}

export const getABTestGroup = () => localStorageService?.getItem(GOOGLE_AB_EXPERIMENT_ID) as ABTestGroups;
export const setABTestGroup = (value: ABTestGroups) => localStorageService?.setItem(GOOGLE_AB_EXPERIMENT_ID, value);

export type StreakTestGroup = 'streak' | 'control';
export function getStreakTestGroup(userId: string): StreakTestGroup {
  if (!userId.length) {
    throw Error('Wrong userId format in getStreakTestGroup');
  }
  return parseInt(userId.slice(-1), 10) % 2 ? 'streak' : 'control';
}

export default analytics;
